import { useState, useEffect } from 'react';

/**
 * Platform Detection Utility for MyCarParkedWhere.com
 * 
 * This utility detects different platform modes and adjusts the footer positioning accordingly:
 * 
 * 1. iOS PWA (Bookmarked/Installed): Footer positioned 10px from bottom, layout padding 90px
 *    - Detected when: iOS device + PWA display mode
 *    - Issue: iOS Safari PWA mode has different viewport handling
 * 
 * 2. iOS Safari Browser: Footer positioned at bottom (0px), layout padding 80px
 *    - Detected when: iOS device + not PWA mode
 *    - Normal browser behavior on iOS
 * 
 * 3. Other PWA platforms: Footer positioned 5px from bottom, layout padding 85px
 *    - Detected when: Any device + PWA mode (not iOS)
 *    - Slight adjustment for other PWA implementations
 * 
 * 4. Regular browsers: Footer positioned at bottom (0px), layout padding 80px
 *    - Default case for all other scenarios
 *    - Standard web app behavior
 */

export interface PlatformInfo {
  isIOS: boolean;
  isPWA: boolean;
  isSafari: boolean;
  isIOSPWA: boolean;
}

// Hook to detect platform and display mode
export const usePlatformDetection = () => {
  const [platformInfo, setPlatformInfo] = useState<PlatformInfo>({
    isIOS: false,
    isPWA: false,
    isSafari: false,
    isIOSPWA: false
  });

  useEffect(() => {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS/.test(userAgent);
    
    // Check if running as PWA (installed/bookmarked)
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  window.matchMedia('(display-mode: fullscreen)').matches ||
                  (window.navigator as any).standalone === true;
    
    const isIOSPWA = isIOS && isPWA;

    const info = {
      isIOS,
      isPWA,
      isSafari,
      isIOSPWA
    };

    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Platform Detection:', {
        userAgent,
        ...info,
        displayMode: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser',
        standalone: (window.navigator as any).standalone
      });
    }

    setPlatformInfo(info);
  }, []);

  return platformInfo;
};

// Utility functions for consistent spacing
export const getFooterBottomPosition = (platformInfo: PlatformInfo): string => {
  const position = (() => {
    if (platformInfo.isIOSPWA) {
      // iOS PWA (bookmarked/installed app) - position slightly higher
      return "10px";
    } else if (platformInfo.isIOS && !platformInfo.isPWA) {
      // iOS Safari browser - normal position
      return "0";
    } else if (platformInfo.isPWA) {
      // Other PWA platforms - slightly adjusted
      return "5px";
    } else {
      // Regular browser on any platform
      return "0";
    }
  })();

  if (process.env.NODE_ENV === 'development') {
    console.log('📍 Footer Position:', position, platformInfo);
  }

  return position;
};

export const getLayoutBottomPadding = (platformInfo: PlatformInfo): string => {
  const padding = (() => {
    if (platformInfo.isIOSPWA) {
      // iOS PWA (bookmarked/installed app) - extra padding for larger footer that extends to bottom
      return "120px";
    } else if (platformInfo.isIOS && !platformInfo.isPWA) {
      // iOS Safari browser - padding for footer at bottom
      return "90px";
    } else if (platformInfo.isPWA) {
      // Other PWA platforms - footer at bottom
      return "95px";
    } else {
      // Regular browser on any platform - footer at bottom
      return "85px";
    }
  })();

  if (process.env.NODE_ENV === 'development') {
    console.log('📏 Layout Padding:', padding, platformInfo);
  }

  return padding;
};

// New utility for footer styling
export const getFooterPadding = (platformInfo: PlatformInfo): string => {
  const padding = (() => {
    if (platformInfo.isIOSPWA) {
      // iOS PWA - extend footer to bottom with extra padding for home indicator
      return "20px 16px 32px 16px";
    } else if (platformInfo.isIOS) {
      // iOS Safari browser - standard mobile padding
      return "12px 16px 16px 16px";
    } else {
      // Regular padding for other platforms
      return "8px 16px 12px 16px";
    }
  })();

  if (process.env.NODE_ENV === 'development') {
    console.log('🎨 Footer Padding:', padding, platformInfo);
  }

  return padding;
};

export const getFooterBorderRadius = (platformInfo: PlatformInfo): string => {
  const radius = (() => {
    if (platformInfo.isIOSPWA) {
      // iOS PWA - remove bottom border radius for seamless look
      return "2xl 2xl 0 0";
    } else {
      // Regular border radius for other platforms
      return "2xl";
    }
  })();

  if (process.env.NODE_ENV === 'development') {
    console.log('🔄 Footer Border Radius:', radius, platformInfo);
  }

  return radius;
}; 